import { budgetAlert, BudgetFeedback } from "./BudgetAlert";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { BudgetService } from "_services/budget.service";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

const titles = { capital: "หมวดงบลงทุน", operating: "หมวดงบดำเนินงาน", subcategory: "หมวดย่อยงบดำเนินงาน" };
const blank = () => ({ label: "", sortOrder: 1, categoryKey: "", allowFileUpload: false });

export default function BudgetCategorySettings({ kind }) {
    const [data, setData] = useState({ capital: [], operating: [], subcategory: [] });
    const [canManage, setCanManage] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [editing, setEditing] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(blank);
    const [filter, setFilter] = useState("");
    const [search, setSearch] = useState("");
    const load = async () => {
        const res = await BudgetService.getCategorySettings();
        if (!res.data.success) throw new Error(res.data.message || "ไม่สามารถโหลดข้อมูลได้");
        setData(res.data.data);
        setCanManage(res.data.canManage === true);
    };
    useEffect(() => {
        Modal.setAppElement(document.getElementById("root") || document.body);
        let active = true;
        BudgetService.getCategorySettings().then(res => {
            if (!res.data.success) throw new Error("ไม่สามารถโหลดข้อมูลได้");
            if (active) { setData(res.data.data); setCanManage(res.data.canManage === true); }
        }).catch(e => { if (active) setError(e.response?.data?.message || e.message); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);
    const reset = () => { setEditing(null); setForm(blank()); setModalOpen(false); };
    const closeModal = () => { if (!busy) { reset(); setError(""); } };
    const openModal = (row = null) => {
        if (busy || !canManage) return;
        setEditing(row ? row.key : null);
        setForm(row ? { label: row.label, sortOrder: row.sortOrder, categoryKey: row.categoryKey || "", allowFileUpload: row.allowFileUpload === true } : blank());
        setError(""); setMessage(""); setModalOpen(true);
    };
    const save = async e => {
        e.preventDefault();
        if (!canManage || busy) return;
        setBusy(true); setError(""); setMessage("");
        try {
            const payload = { label: form.label.trim(), sortOrder: Number(form.sortOrder),
                categoryKey: kind === "subcategory" ? Number(form.categoryKey) : null,
                ...(kind === "subcategory" ? { allowFileUpload: form.allowFileUpload } : {}) };
            const res = editing === null ? await BudgetService.createCategory(kind, payload)
                : await BudgetService.updateCategory(kind, editing, payload);
            if (!res.data.success) throw new Error(res.data.message || "บันทึกไม่สำเร็จ");
            reset();
            await load();
            setMessage("บันทึกข้อมูลเรียบร้อยแล้ว");
        } catch (e) { setError(e.response?.data?.message || e.message); }
        finally { setBusy(false); }
    };
    const remove = async row => {
        if (!canManage || busy || !await budgetAlert.confirm(`ยืนยันลบ “${row.label}” หรือไม่?`)) return;
        setBusy(true); setError(""); setMessage("");
        try {
            const res = await BudgetService.deleteCategory(kind, row.key);
            if (!res.data.success) throw new Error(res.data.message || "ลบไม่สำเร็จ");
            if (editing === row.key) reset();
            await load(); setMessage("ลบข้อมูลเรียบร้อยแล้ว");
        } catch (e) { setError(e.response?.data?.message || e.message); }
        finally { setBusy(false); }
    };
    const parentName = key => (data.operating.find(c => c.key === key) || {}).label || "ไม่พบหมวดหลัก";
    const rows = data[kind].filter(row => (!filter || row.categoryKey === Number(filter)) &&
        row.label.toLowerCase().includes(search.toLowerCase()));
    return <Card>
        <CardHeader color="info"><h4 style={{ color: "white", margin: 0 }}>ตั้งค่า / {titles[kind]}</h4></CardHeader>
        <CardBody>
            <BudgetFeedback message={error} />
            <BudgetFeedback message={message} type="success" />
            {loading ? <p>กำลังโหลดข้อมูล...</p> : <>
                {!canManage && <p className="text-muted">ดูข้อมูลได้เท่านั้น การจัดการหมวดสงวนสิทธิ์สำหรับงานแผนงานและงานคอมพิวเตอร์</p>}
                {canManage && <button type="button" className="btn btn-info mb-3" disabled={busy} onClick={() => openModal()}>เพิ่มรายการ</button>}
                <Modal isOpen={canManage && modalOpen} onRequestClose={closeModal}
                    shouldCloseOnOverlayClick={!busy} shouldCloseOnEsc={!busy} aria={{ labelledby: "category-modal-title" }}
                    style={{ overlay: { backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1300 }, content: {
                        top: "50%", left: "50%", right: "auto", bottom: "auto", transform: "translate(-50%, -50%)",
                        width: "520px", maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto", borderRadius: "8px", padding: "24px"
                    } }}>
                    <form onSubmit={save}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 id="category-modal-title" className="mb-0">{editing === null ? "เพิ่ม" : "แก้ไข"}{titles[kind]}</h5>
                        <button type="button" className="btn btn-link p-0" aria-label="ปิดหน้าต่าง" disabled={busy} onClick={closeModal}>✕</button>
                    </div>
                    
                    <fieldset disabled={busy}>
                        {kind === "subcategory" && <div className="form-group">
                            <label htmlFor="category-parent">หมวดงบดำเนินงาน *</label>
                            <select id="category-parent" className="form-control" required value={form.categoryKey}
                                onChange={e => setForm({ ...form, categoryKey: e.target.value })}>
                                <option value="">เลือกหมวดหลัก</option>
                                {data.operating.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </div>}
                        {kind === "subcategory" && <div className="form-group"><label>
                            <input type="checkbox" checked={form.allowFileUpload} onChange={e => setForm({ ...form, allowFileUpload: e.target.checked })} /> เปิดให้อัปโหลด PDF หลายไฟล์
                        </label></div>}
                        <div className="form-group"><label htmlFor="category-label">ชื่อหมวด *</label>
                            <input id="category-label" className="form-control" required maxLength={200} value={form.label}
                                onChange={e => setForm({ ...form, label: e.target.value })} /></div>
                        <div className="form-group"><label htmlFor="category-order">ลำดับแสดงผล *</label>
                            <input id="category-order" className="form-control" type="number" min="0" max="2147483647" step="1" required value={form.sortOrder}
                                onChange={e => setForm({ ...form, sortOrder: e.target.value })} /></div>
                        <button className="btn btn-info mr-2" type="submit">{busy ? "กำลังดำเนินการ..." : "บันทึก"}</button>
                        <button className="btn btn-secondary" type="button" onClick={closeModal}>ยกเลิก</button>
                    </fieldset>
                    </form>
                </Modal>
                <div className="form-group"><label htmlFor="category-search">ค้นหาชื่อหมวด</label>
                    <input id="category-search" className="form-control" value={search} onChange={e => setSearch(e.target.value)} /></div>
                {kind === "subcategory" && <div className="form-group"><label htmlFor="category-filter">กรองตามหมวดหลัก</label>
                    <select id="category-filter" className="form-control" value={filter} onChange={e => setFilter(e.target.value)}>
                        <option value="">ทุกหมวดหลัก</option>
                        {data.operating.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select></div>}
                <div className="table-responsive"><table className="table table-striped">
                    <thead><tr><th>รหัส</th>{kind === "subcategory" && <th>หมวดหลัก</th>}<th>ชื่อหมวด</th><th>ลำดับ</th>{canManage && <th>จัดการ</th>}</tr></thead>
                    <tbody>{rows.map(row => <tr key={row.key}>
                        <td>{row.key}</td>{kind === "subcategory" && <td>{parentName(row.categoryKey)}</td>}<td>{row.label}{kind === "subcategory" && row.allowFileUpload && <small className="d-block text-info">เปิดให้อัปโหลด PDF หลายไฟล์</small>}</td><td>{row.sortOrder}</td>
                        {canManage && <td><button className="btn btn-sm btn-info mr-2" disabled={busy} onClick={() => openModal(row)} aria-label={`แก้ไข ${row.label}`}>แก้ไข</button>
                            <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => remove(row)} aria-label={`ลบ ${row.label}`}>ลบ</button></td>}
                    </tr>)}{rows.length === 0 && <tr><td colSpan={kind === "subcategory" ? 5 : 4}>ไม่พบรายการ</td></tr>}</tbody>
                </table></div>
            </>}
        </CardBody>
    </Card>;
}
